import { httpClient } from '@/shared/api';
import type {
  GroupComparisonsCohortDistribution,
  GroupComparisonsFilterParams,
  GroupComparisonsFilterResult,
  GroupComparisonsMetricFocus,
  GroupComparisonsSchoolRow,
  GroupComparisonsSummary,
} from './groupComparisons.dto';

const PREFIX = '/app/group-comparison/group-comparisons';

/**
 * Backend `GroupComparisonMetricEnum`:
 * 1 = CostPerStudent, 2 = TuitionPerStudent, 3 = AvgTeacherSalary
 */
export const GROUP_COMPARISON_METRIC_TO_API: Record<GroupComparisonsMetricFocus, number> = {
  cost: 1,
  tuition: 2,
  salary: 3,
};

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readNullableNum(o: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = o[k];
    if (v == null) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function readBool(o: Record<string, unknown>, keys: string[]): boolean {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'boolean') return v;
    if (v === 'true') return true;
    if (v === 'false') return false;
  }
  return false;
}

function readStrArray(o: Record<string, unknown>, keys: string[]): string[] {
  for (const k of keys) {
    const v = o[k];
    if (!Array.isArray(v)) continue;
    return v.map((x) => String(x ?? '').trim()).filter(Boolean);
  }
  return [];
}

function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('result' in o && o.result != null && typeof o.result === 'object') {
    return o.result as Record<string, unknown>;
  }
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as Record<string, unknown>;
  }
  return o;
}

function mapSummary(raw: unknown): GroupComparisonsSummary {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    schoolCount: readNum(o, ['schoolCount', 'SchoolCount']) ?? 0,
    outlierCount: readNum(o, ['outlierCount', 'OutlierCount']) ?? 0,
    peerMedianCostUsd: readNum(o, ['peerMedianCostUsd', 'PeerMedianCostUsd']) ?? 0,
    peerMedianTuitionUsd: readNum(o, ['peerMedianTuitionUsd', 'PeerMedianTuitionUsd']) ?? 0,
    peerMedianSalaryUsd: readNum(o, ['peerMedianSalaryUsd', 'PeerMedianSalaryUsd']) ?? 0,
  };
}

function mapDistribution(raw: unknown): GroupComparisonsCohortDistribution {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    peerMedian: readNum(o, ['peerMedian', 'PeerMedian']) ?? 0,
    p25: readNum(o, ['p25', 'P25']) ?? 0,
    p75: readNum(o, ['p75', 'P75']) ?? 0,
    min: readNum(o, ['min', 'Min']) ?? 0,
    max: readNum(o, ['max', 'Max']) ?? 0,
  };
}

function mapRow(raw: unknown): GroupComparisonsSchoolRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const schoolId = readStr(o, ['schoolId', 'SchoolId']);
  if (!schoolId) return null;

  return {
    schoolId,
    schoolCode: readStr(o, ['schoolCode', 'SchoolCode']),
    schoolName: readStr(o, ['schoolName', 'SchoolName']),
    governorateId: readNum(o, ['governorateId', 'GovernorateId', 'governerateId', 'GovernerateId']) ?? 0,
    governorateName: readStr(o, ['governorateName', 'GovernorateName', 'governerateName', 'GovernerateName']),
    studentsCount: readNum(o, ['studentsCount', 'StudentsCount']) ?? 0,
    costPerStudentUsd: readNum(o, ['costPerStudentUsd', 'CostPerStudentUsd']) ?? 0,
    tuitionPerStudentUsd: readNum(o, ['tuitionPerStudentUsd', 'TuitionPerStudentUsd']) ?? 0,
    avgTeacherSalaryUsd: readNum(o, ['avgTeacherSalaryUsd', 'AvgTeacherSalaryUsd']) ?? 0,
    peerMedianCostUsd: readNum(o, ['peerMedianCostUsd', 'PeerMedianCostUsd']) ?? 0,
    peerMedianTuitionUsd: readNum(o, ['peerMedianTuitionUsd', 'PeerMedianTuitionUsd']) ?? 0,
    peerMedianSalaryUsd: readNum(o, ['peerMedianSalaryUsd', 'PeerMedianSalaryUsd']) ?? 0,
    peerMinCostUsd: readNum(o, ['peerMinCostUsd', 'PeerMinCostUsd']) ?? 0,
    peerMaxCostUsd: readNum(o, ['peerMaxCostUsd', 'PeerMaxCostUsd']) ?? 0,
    peerMinTuitionUsd: readNum(o, ['peerMinTuitionUsd', 'PeerMinTuitionUsd']) ?? 0,
    peerMaxTuitionUsd: readNum(o, ['peerMaxTuitionUsd', 'PeerMaxTuitionUsd']) ?? 0,
    peerMinSalaryUsd: readNum(o, ['peerMinSalaryUsd', 'PeerMinSalaryUsd']) ?? 0,
    peerMaxSalaryUsd: readNum(o, ['peerMaxSalaryUsd', 'PeerMaxSalaryUsd']) ?? 0,
    varianceCostPct: readNullableNum(o, ['varianceCostPct', 'VarianceCostPct']),
    varianceTuitionPct: readNullableNum(o, ['varianceTuitionPct', 'VarianceTuitionPct']),
    varianceSalaryPct: readNullableNum(o, ['varianceSalaryPct', 'VarianceSalaryPct']),
    budgetSubmitted: readBool(o, ['budgetSubmitted', 'BudgetSubmitted']),
    isOutlier: readBool(o, ['isOutlier', 'IsOutlier']),
    outlierReasons: readStrArray(o, ['outlierReasons', 'OutlierReasons']),
    schoolBandId: readStr(o, ['schoolBandId', 'SchoolBandId', 'schoolbandId', 'SchoolbandId']),
    schoolBandName: readStr(o, ['schoolBandName', 'SchoolBandName']),
    tuitionBandId: readStr(o, ['tuitionBandId', 'TuitionBandId']),
    tuitionBandName: readStr(o, ['tuitionBandName', 'TuitionBandName']),
    parentBandId: readStr(o, ['parentBandId', 'ParentBandId', 'parentTuitionBandId', 'ParentTuitionBandId']),
    parentBandName: readStr(o, ['parentBandName', 'ParentBandName', 'parentTuitionBandName', 'ParentTuitionBandName']),
  };
}

export function mapGroupComparisonsFilterResult(raw: unknown): GroupComparisonsFilterResult {
  const o = unwrapPayload(raw) ?? {};
  const rawRows = (o.rows ?? o.Rows) as unknown[] | undefined;
  const rows = Array.isArray(rawRows)
    ? rawRows.map(mapRow).filter((r): r is GroupComparisonsSchoolRow => r != null)
    : [];

  return {
    composedPeerGroupLabel: readStr(o, ['composedPeerGroupLabel', 'ComposedPeerGroupLabel']),
    summary: mapSummary(o.summary ?? o.Summary),
    distribution: mapDistribution(o.distribution ?? o.Distribution),
    rows,
  };
}

export type GroupComparisonsApiParams = {
  year: number;
  governorateId?: number;
  schoolBandId?: string;
  tuitionBandId?: string;
  parentBandId?: string;
  metricFocus: GroupComparisonsMetricFocus;
};

function buildQuery(params: GroupComparisonsApiParams): string {
  const q = new URLSearchParams();
  q.set('Year', String(params.year));
  q.set('Metric', String(GROUP_COMPARISON_METRIC_TO_API[params.metricFocus]));
  if (params.governorateId != null && Number.isFinite(params.governorateId)) {
    q.set('governerateId', String(params.governorateId));
  }
  if (params.schoolBandId?.trim()) {
    q.set('SchoolSizeBandId', params.schoolBandId.trim());
  }
  if (params.tuitionBandId?.trim()) {
    q.set('TuitionBandId', params.tuitionBandId.trim());
  }
  if (params.parentBandId?.trim()) {
    q.set('ParentTuitionBandId', params.parentBandId.trim());
  }
  return q.toString();
}

export function filterParamsToApiParams(params: GroupComparisonsFilterParams): GroupComparisonsApiParams | null {
  const year = parseInt(params.year.trim(), 10);
  if (!Number.isFinite(year)) return null;

  return {
    year,
    governorateId: params.governorateId,
    schoolBandId: params.schoolBandId,
    tuitionBandId: params.tuitionBandId,
    parentBandId: params.parentBandId,
    metricFocus: params.metricFocus,
  };
}

export const groupComparisonsApi = {
  getGroupComparisons: async (params: GroupComparisonsApiParams): Promise<GroupComparisonsFilterResult> => {
    const query = buildQuery(params);
    const res = await httpClient.get<unknown>(`${PREFIX}?${query}`);
    return mapGroupComparisonsFilterResult(res);
  },
};
